/** Reply-targeting §3 + shared spines P2 in-flight chrome — per-RESIDENT
 *  pending entries, held client-side from the ack's dispatched[] until the
 *  TURN_ADDED reload repaints. campArrivalState is permanently 'resolved'
 *  once a camp has any peer child (campArrivalState.ts:23), so this is the
 *  ONLY source of "a resident is thinking about a reply". Pure module — no
 *  React, no api. */
import { residentNamesCopy } from './trailResidents';

export interface PendingReply {
  envelopeMessageId: string;
  spineRootMessageId: string;
  /** P2: which resident this entry tracks (null = pre-P2 ack fallback —
   *  resolution then accepts ANY peer answer at the envelope). */
  personaId: string | null;
  /** P2: the trail the reply rode — pill lookups are trail-keyed. */
  trailId: string | null;
  residentName: string | null;
  sinceTs: number;
}

interface MinimalMsg {
  id: string;
  parent_ids?: string[];
  author_kind?: string;
  metadata?: Record<string, any>;
}

const _key = (p: { envelopeMessageId: string; spineRootMessageId: string }) =>
  `${p.envelopeMessageId}:${p.spineRootMessageId}`;

export function addPending(
  list: PendingReply[], entry: PendingReply,
): PendingReply[] {
  // Composite key: one envelope fans to N residents (P2) — siblings coexist.
  return [...list.filter((p) => _key(p) !== _key(entry)), entry];
}

/** Reload self-heal: an entry resolves when ITS resident answered its
 *  envelope — matched via the reply_to_message_id stamp (answers tip-append,
 *  so parentage can point at a sibling answer), with the parent-keyed check
 *  as a pre-stamp fallback. Stale pendings drop when the envelope vanishes.
 *  Failure path (F7): a turn_failed-stamped FR/camp marker also drops its pending
 *  (else a failed fork zombies the compose pill as "reading the trail…"). */
export function clearResolved(
  list: PendingReply[], messages: MinimalMsg[],
): PendingReply[] {
  const ids = new Set(messages.map((m) => m.id));
  // F7 — a failed fork/turn stamps metadata.turn_failed on its FR/camp marker; drop
  // the pending so the pill clears (the camp skeleton clears via the reload →
  // campArrivalState 'failed' state).
  const failedSpines = new Set(
    messages.filter((m) => m.metadata?.turn_failed).map((m) => m.id));
  return list.filter((p) => {
    if (!ids.has(p.envelopeMessageId)) return false;
    if (failedSpines.has(p.spineRootMessageId)) return false;
    const answered = messages.some((m) =>
      m.author_kind === 'peer'
      && (p.personaId == null || m.metadata?.persona_id === p.personaId)
      && (m.metadata?.reply_to_message_id === p.envelopeMessageId
          || m.parent_ids?.[0] === p.envelopeMessageId));
    return !answered;
  });
}

/** Eternal-spinner backstop (SP-3a follow-up): an async resident turn can die
 *  SILENTLY after the ack — WS death, worker crash — leaving no answer AND no
 *  `turn_failed` stamp, so clearResolved never drains its pending and the
 *  "reading the trail…" pill spins forever. Drop any entry older than maxAgeMs
 *  as a last resort; the reload self-heal (clearResolved) stays the fast path. */
export function dropStale(
  list: PendingReply[], now: number, maxAgeMs: number,
): PendingReply[] {
  return list.filter((p) => now - p.sinceTs < maxAgeMs);
}

export function pendingForSpine(
  list: PendingReply[], spineRootMessageId: string | null,
): PendingReply[] {
  if (!spineRootMessageId) return [];
  return list.filter((p) => p.spineRootMessageId === spineRootMessageId);
}

/** P2: the pill cares about the TRAIL — co-residents camp on their own spines. */
export function pendingForTrail(
  list: PendingReply[], trailId: string | null,
): PendingReply[] {
  if (!trailId) return [];
  return list.filter((p) => p.trailId === trailId);
}

/** Distinct displayable names, insertion order — drives the morph copy. */
export function pendingResidentNames(list: PendingReply[]): string[] {
  return Array.from(new Set(
    list.map((p) => p.residentName).filter(Boolean))) as string[];
}

/** P2 — the pill's post-send morph, as data. ONE formatter (residentNamesCopy)
 *  so copy can't drift across surfaces; `queued` keys on DISTINCT envelopes
 *  (N residents fanning ONE send is not a queue). Null = nothing pending.
 *  Pure extraction: ConversationInterface.test.tsx is a standing import.meta
 *  red, so the morph logic is unit-tested HERE and visually via e2e. */
export function morphState(
  list: PendingReply[], fallbackName: string | null,
): { copy: string; queued: boolean } | null {
  if (list.length === 0) return null;
  const names = pendingResidentNames(list);
  const joined = residentNamesCopy(names);
  const copy = names.length > 1
    ? `${joined} are reading the trail…`
    : `${joined ?? fallbackName ?? 'They'} is reading the trail…`;
  return {
    copy,
    queued: new Set(list.map((p) => p.envelopeMessageId)).size > 1,
  };
}
